import {
  Offer,
  OfferPriceVisibility,
} from "@/services/backend/qpm/offers/types/offer";

export function resolvedPriceVisibility(offer: Offer): OfferPriceVisibility {
  return (
    offer.priceVisibility ?? {
      material: { qpm: true, fiv: true, aLoRa: true, additions: true },
      installation: { qpm: true, fiv: true, aLoRa: true },
      maintenance: {
        qpm: true,
        fiv: true,
        scb: true,
        aLoRa: offer.includeIoTCheckpointServiceInQpm ?? false,
      },
      renting: { aLoRa: true },
    }
  );
}

export type OfferPriceAvailability = {
  material: { qpm: boolean; fiv: boolean; aLoRa: boolean; additions: boolean };
  installation: { qpm: boolean; fiv: boolean; aLoRa: boolean };
  maintenance: { qpm: boolean; fiv: boolean; scb: boolean; aLoRa: boolean };
  renting: { aLoRa: boolean };
};

export function resolvedPriceAvailability(
  offer: Offer,
): OfferPriceAvailability {
  const qpmCohorts = offer.maintenanceResult.qpmCohorts ?? [];
  const qpmMaintenanceTotal = (
    qpmCohorts.length > 0 ? qpmCohorts : [offer.maintenanceResult.qpm]
  ).reduce((sum, row) => sum + row.totalCost, 0);

  return {
    material: {
      qpm: offer.materialCost.qpm.totalCost > 0,
      fiv: offer.materialCost.fiv.totalCost > 0,
      aLoRa: offer.materialCost.aLoRa.totalCost > 0,
      additions: offer.materialCost.additions.totalCost > 0,
    },
    installation: {
      qpm: offer.installationResult.qpm.totalCost > 0,
      fiv: offer.installationResult.fiv.totalCost > 0,
      aLoRa: offer.installationResult.aLoRa.totalCost > 0,
    },
    maintenance: {
      qpm: qpmMaintenanceTotal > 0,
      fiv: offer.flyExterminatorMaintenance.cohorts.some(
        (cohort) =>
          cohort.discountedPricePerMeasure > 0 || cohort.pricePerMeasure > 0,
      ),
      scb: offer.maintenanceResult.scb.totalCost > 0,
      aLoRa:
        offer.maintenanceResult.qpmIoTAddon.totalCost > 0 ||
        offer.maintenanceResult.aLoRa.totalCost > 0,
    },
    renting: {
      aLoRa:
        offer.rentingResult.rentPerMonth > 0 ||
        offer.rentingResult.rentPerMonthWithMaintenance > 0,
    },
  };
}

export function hasVisibleInstallationSection(offer: Offer): boolean {
  const availability = resolvedPriceAvailability(offer);

  return (
    availability.material.qpm ||
    availability.material.fiv ||
    availability.material.additions ||
    availability.material.aLoRa ||
    availability.installation.qpm ||
    availability.installation.fiv ||
    availability.installation.aLoRa
  );
}

export function hasVisibleMaintenanceSection(offer: Offer): boolean {
  const availability = resolvedPriceAvailability(offer);

  return (
    availability.maintenance.qpm ||
    availability.maintenance.scb ||
    availability.maintenance.aLoRa
  );
}

export function hasVisibleFlyExterminatorMaintenanceSection(
  offer: Offer,
): boolean {
  const availability = resolvedPriceAvailability(offer);

  return availability.maintenance.fiv;
}

export function hasVisibleRentingSection(offer: Offer): boolean {
  const availability = resolvedPriceAvailability(offer);

  return availability.renting.aLoRa;
}
